import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { tap, catchError } from 'rxjs';
import { throwError } from 'rxjs';
import { ISipping, IGetShippingResponse, Pagination } from '../../interfaces/deliveries.interface';
import { ReturnsService } from '../../services/returns/returns.service';
import { ReturnsActions } from './returns.actions';
import { IGetReturnByID, IGetReturnsPaginatedResponse } from '../../interfaces/returns.interface';
import { UploadProgress } from '../../services/file-upload/file-upload.service';
import { FileUploadService } from '../../services/file-upload/file-upload.service';

export interface ReturnsStateModel {
  returns: Pagination<IGetReturnByID>;
  loading: boolean;
  return: IGetReturnByID | null;
  uploadProgress: UploadProgress | null;
  uploading: boolean;
  uploadError: string | null;
  highlightedId: number | null;
}

@State<ReturnsStateModel>({
  name: 'returns',
  defaults: {
    returns: { items: [], page: 1, pageSize: 10, total: 0 },
    loading: false,
    return: null,
    uploadProgress: null,
    uploading: false,
    uploadError: null,
    highlightedId: null,
  },
})

@Injectable()
export class ReturnsState {
  constructor(
    private returnsService: ReturnsService,
    private fileUploadService: FileUploadService
  ) {}

  @Selector()
  static getReturns(state: ReturnsStateModel): Pagination<IGetReturnByID> {
    return state.returns;
  }

  @Selector()
  static isLoading(state: ReturnsStateModel): boolean {
    return state.loading;
  }

  @Selector()
  static getReturn(state: ReturnsStateModel): IGetReturnByID | null {
    return state.return;
  }

  @Selector()
  static getUploadProgress(state: ReturnsStateModel): UploadProgress | null {
    return state.uploadProgress;
  }

  @Selector()
  static isUploading(state: ReturnsStateModel): boolean {
    return state.uploading;
  }

  @Selector()
  static getUploadError(state: ReturnsStateModel): string | null {
    return state.uploadError;
  }

  @Selector()
  static getHighlightedId(state: ReturnsStateModel): number | null {
    return state.highlightedId;
  }

  @Action(ReturnsActions.GetReturns)
  getReturns(ctx: StateContext<ReturnsStateModel>, action: ReturnsActions.GetReturns) {
    ctx.patchState({ loading: true });

    return this.returnsService.getReturns(action.page, action.pageSize, action.status, action.priority, action.q, action.carrier, action.driverId).pipe(
      tap({
        next: (response: IGetReturnsPaginatedResponse) => {
          ctx.patchState({
            returns: response.data,
            loading: false,
          });
        },
        error: () => {
          ctx.patchState({
            loading: false,
          });
        },
      })
    );
  }

  @Action(ReturnsActions.SetReturns)
  setReturns(
    ctx: StateContext<ReturnsStateModel>,
    action: ReturnsActions.SetReturns
  ) {
    ctx.patchState({
      returns: action.returns,
    });
  }

  @Action(ReturnsActions.GetReturnById)
  getReturnById(
    ctx: StateContext<ReturnsStateModel>,
    action: ReturnsActions.GetReturnById
  ) {
    ctx.patchState({ loading: true });
    return this.returnsService.getReturnById(action.id).pipe(
      tap({
        next: (response) => {
          ctx.patchState({
            return: response.data,
            loading: false,
          });
        },
        error: () => {
          ctx.patchState({
            loading: false,
          });
        },
      })
    );
  }

  @Action(ReturnsActions.UploadReturnFile)
  uploadReturnFile(
    ctx: StateContext<ReturnsStateModel>,
    action: ReturnsActions.UploadReturnFile
  ) {
    // Validate file first
    const validation = this.fileUploadService.validateFile(action.file);
    if (!validation.valid) {
      ctx.dispatch(new ReturnsActions.UploadReturnFileError(validation.error!));
      return;
    }

    ctx.patchState({
      uploading: true,
      uploadError: null,
      uploadProgress: null
    });

    return this.returnsService.uploadFile(action.returnId, action.file).pipe(
      tap(event => {
        const progress = this.fileUploadService.getUploadProgress(event);
        if (progress) {
          ctx.dispatch(new ReturnsActions.UploadReturnFileProgress(progress));
        }

        if (this.fileUploadService.isUploadComplete(event)) {
          const response = event as any;
          ctx.dispatch(new ReturnsActions.UploadReturnFileSuccess(response.body));
        }
      }),
      catchError(error => {
        ctx.dispatch(new ReturnsActions.UploadReturnFileError(error.message));
        return throwError(() => error);
      })
    );
  }

  @Action(ReturnsActions.UploadReturnFileProgress)
  uploadReturnFileProgress(
    ctx: StateContext<ReturnsStateModel>,
    action: ReturnsActions.UploadReturnFileProgress
  ) {
    ctx.patchState({ uploadProgress: action.progress });
  }

  @Action(ReturnsActions.UploadReturnFileSuccess)
  uploadReturnFileSuccess(
    ctx: StateContext<ReturnsStateModel>,
    action: ReturnsActions.UploadReturnFileSuccess
  ) {
    ctx.patchState({
      uploading: false,
      uploadProgress: null,
      uploadError: null
    });

    // Refrescar los datos de la devolución para mostrar el nuevo archivo
    const state = ctx.getState();
    if (state.return?.id) {
      ctx.dispatch(new ReturnsActions.GetReturnById(state.return.id.toString()));
    }
  }

  @Action(ReturnsActions.UploadReturnFileError)
  uploadReturnFileError(
    ctx: StateContext<ReturnsStateModel>,
    action: ReturnsActions.UploadReturnFileError
  ) {
    ctx.patchState({
      uploading: false,
      uploadProgress: null,
      uploadError: action.error
    });
  }

  @Action(ReturnsActions.HighlightRow)
  highlightRow(
    ctx: StateContext<ReturnsStateModel>,
    action: ReturnsActions.HighlightRow
  ) {
    ctx.patchState({ highlightedId: action.id });
  }

  @Action(ReturnsActions.PatchReturn)
  patchReturn(
    ctx: StateContext<ReturnsStateModel>,
    action: ReturnsActions.PatchReturn
  ) {
    const state = ctx.getState();
    const items = state.returns.items.map((item) =>
      item.id === action.item.id ? { ...item, ...action.item } : item
    );
    ctx.patchState({
      returns: { ...state.returns, items },
    });
  }
}
